/**
 * Talks to the portable security-monitoring endpoint on your own host.
 *
 * Every call POSTs `application/x-www-form-urlencoded` with an `action` field
 * and expects a JSON envelope:
 *     { "success": true, "code": "OK", "message": "...", "data": { ... } }
 */
import { SECURITY_ENDPOINT, SECURITY_KEY_HEADER, SECURITY_KEY_STORAGE } from "@/config/security";

export type AlertSeverity = "low" | "medium" | "high" | "critical";
export type AlertStatus = "open" | "resolved";

export interface SecurityAlert {
  id: string;
  type: string;
  severity: AlertSeverity;
  message: string;
  meta?: Record<string, unknown>;
  createdAt: string;
  status: AlertStatus;
  resolvedAt?: string | null;
}

export interface SecurityStats {
  total: number;
  open: number;
  resolved: number;
  bySeverity: Record<string, number>;
  lastScan?: string | null;
  integrity?: {
    status: "ok" | "changed" | "no_baseline";
    checkedAt?: string | null;
    changed?: number;
  };
}

export interface IntegrityResult {
  status: "ok" | "changed" | "no_baseline";
  checkedAt: string;
  totalFiles: number;
  changedFiles: string[];
  addedFiles: string[];
  removedFiles: string[];
}

export interface SpamScanResult {
  scanned: number;
  flagged: number;
  checkedAt: string;
  findings: { id: string; reasons: string[] }[];
}

interface ApiEnvelope {
  success?: boolean;
  code?: string;
  message?: string;
  data?: unknown;
}

export const getAdminKey = (): string => {
  try {
    return sessionStorage.getItem(SECURITY_KEY_STORAGE) || "";
  } catch {
    return "";
  }
};

export const setAdminKey = (key: string): void => {
  try {
    sessionStorage.setItem(SECURITY_KEY_STORAGE, key);
  } catch {
    /* sessionStorage unavailable — the operator will be asked again */
  }
};

export const clearAdminKey = (): void => {
  try {
    sessionStorage.removeItem(SECURITY_KEY_STORAGE);
  } catch {
    /* noop */
  }
};

async function call<T>(action: string, params: Record<string, string> = {}): Promise<T> {
  const body = new URLSearchParams({ action, ...params });
  let response: Response;
  try {
    response = await fetch(SECURITY_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
        [SECURITY_KEY_HEADER]: getAdminKey(),
      },
      body: body.toString(),
    });
  } catch {
    throw new Error(
      "Could not reach the security endpoint. Check VITE_PUBLIC_SECURITY_ENDPOINT and that your backend is running.",
    );
  }

  const text = await response.text();
  let parsed: ApiEnvelope | null = null;
  try {
    parsed = JSON.parse(text) as ApiEnvelope;
  } catch {
    parsed = null;
  }

  if (!response.ok || !parsed || parsed.success === false) {
    const message = parsed?.message && parsed.message.trim() ? parsed.message : `Request failed (HTTP ${response.status}).`;
    throw new Error(message);
  }

  return (parsed.data ?? (parsed as unknown)) as T;
}

export const pingSecurity = (): Promise<{ ok: boolean; serverTime?: string }> => call("ping");

export const fetchAlerts = (): Promise<{ alerts: SecurityAlert[] }> => call("alerts");

export const fetchStats = (): Promise<SecurityStats> => call("stats");

export const resolveAlert = (id: string): Promise<{ resolved: boolean }> =>
  call("resolve", { id });

export const triggerSpamScan = (): Promise<SpamScanResult> => call("scan");

export const runIntegrityCheck = (): Promise<IntegrityResult> => call("integrity");

export const rebuildBaseline = (): Promise<{ status: string; totalFiles: number }> =>
  call("baseline");